import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../firebase';
// Firestoreの各機能をインポート
import { collection, getDocs, doc, writeBatch, query, orderBy, serverTimestamp, Timestamp, deleteDoc } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, ListGroup, Tabs, Tab, Table, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Papa from 'papaparse'; // CSV解析ライブラリ

/**
 * 品番を正規化（統一した形式に整える）するヘルパー関数
 */
const normalizePartNumber = (partNumberStr) => {
  if (!partNumberStr) return '';
  const halfWidthStr = partNumberStr.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
  const noHyphenStr = halfWidthStr.replace(/-/g, '');
  return noHyphenStr.toUpperCase();
};

/**
 * 'YYYYMMDD'形式の8桁の文字列をFirebaseのTimestamp形式に変換するヘルパー関数
 */
const parseDateToTimestamp = (dateString) => {
  if (!dateString || String(dateString).trim().length !== 8) return null;
  const str = String(dateString).trim();
  const year = parseInt(str.substring(0, 4), 10);
  const month = parseInt(str.substring(4, 6), 10) - 1;
  const day = parseInt(str.substring(6, 8), 10);
  const date = new Date(year, month, day);
  
  if (isNaN(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
  
  return Timestamp.fromDate(date);
};

/**
 * 表示するページ番号を制限する、高機能なページネーションコンポーネント
 */
const CustomPagination = ({ currentPage, totalPages, paginate }) => {
  const maxPageButtons = 5;
  let startPage, endPage;

  if (totalPages <= maxPageButtons) {
    startPage = 1;
    endPage = totalPages;
  } else {
    const maxPagesBeforeCurrentPage = Math.floor(maxPageButtons / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPageButtons / 2) - 1;
    
    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxPageButtons;
    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPageButtons + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  const pageNumbers = [...Array((endPage - startPage) + 1)].map((_, i) => startPage + i);

  return (
    <Pagination className="justify-content-center">
      <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} />
      <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
      
      {startPage > 1 && (
        <Pagination.Ellipsis onClick={() => paginate(startPage - 1)} />
      )}

      {pageNumbers.map(number => (
        <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
          {number}
        </Pagination.Item>
      ))}

      {endPage < totalPages && (
        <Pagination.Ellipsis onClick={() => paginate(endPage + 1)} />
      )}

      <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} />
      <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} />
    </Pagination>
  );
};

/**
 * 商品管理画面のメインコンポーネント
 */
function ProductAdmin() {
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [loadingLists, setLoadingLists] = useState(true);
  
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
  
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [catalogCurrentPage, setCatalogCurrentPage] = useState(1);
  const itemsPerPage = 15;

  /**
   * Firestoreから商品、カタログ、ユーザー情報を取得する関数
   */
  const fetchData = useCallback(async () => {
    setLoadingLists(true);
    try {
      // ★★★【商品リストの並び替え】第一優先: No.(sequentialNumber)、第二優先: CSV行順(importOrder) ★★★
      const productsQuery = query(
        collection(db, 'products'), 
        orderBy('sequentialNumber', 'asc'),
        orderBy('importOrder', 'asc')
      );
      
      // ★★★【カタログリストの並び替え】第一優先: sequentialNum、第二優先: importOrder(CSV行順) ★★★
      // ※注意: importOrderでソートするには、カタログデータにもimportOrderを保存する必要があります（後述のhandleUploadで対応）
      const catalogsQuery = query(
        collection(db, 'productCatalogs'), 
        orderBy('sequentialNum', 'asc'),
        orderBy('importOrder', 'asc') // カタログにもCSVの出現順を保存してソートに使用
      );
      
      const usersQuery = query(collection(db, 'users'));

      const [productsSnapshot, catalogsSnapshot, usersSnapshot] = await Promise.all([
        getDocs(productsQuery),
        getDocs(catalogsQuery),
        getDocs(usersQuery),
      ]);

      setProducts(productsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      setCatalogs(catalogsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      
      const usersDataMap = new Map();
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        if (userData.email && userData.personName) {
          usersDataMap.set(userData.email, userData.personName);
        }
      });
      setUsersMap(usersDataMap);

    } catch (err) {
      console.error('登録済みリストの取得に失敗しました。詳細:', err);
      // 複合インデックスが必要な場合のエラーメッセージ
      setError('登録済みリストの取得に失敗しました。Firestoreのインデックス設定が必要です。開発者ツールのコンソールに表示されるリンクからインデックスを作成してください。'); 
    }
    setLoadingLists(false);
  }, []);

  useEffect(() => {
    document.title = '商品管理 | My Order System';
    fetchData();
  }, [fetchData]);

  const handleFileChange = (e) => {
    setError('');
    setUploadResult(null);
    if (e.target.files.length) {
      setCsvFile(e.target.files[0]);
    }
  };

  /**
   * CSVアップロード実行ハンドラ
   */
  const handleUpload = () => {
    if (!csvFile) { setError('CSVファイルを選択してください。'); return; }
    
    if (!window.confirm("本当に実行しますか？\n既存の全てのデータが削除され、このCSVの内容に完全に置き換えられます。\nこの操作は元に戻せません。")) {
      return;
    }
    
    setUploading(true);
    setError('');
    setUploadResult(null);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const productsFromCsv = results.data;
        const currentUser = auth.currentUser;
        
        if (!currentUser) { setError('認証情報が見つかりません。'); setUploading(false); return; }
        if (productsFromCsv.length === 0) { setError('CSVに登録可能なデータがありません。'); setUploading(false); return; }

        try {
          const productsCollectionRef = collection(db, 'products');
          const productCatalogsCollectionRef = collection(db, 'productCatalogs');
          
          // --- 既存データの全削除処理 ---
          const deleteProductsPromise = getDocs(productsCollectionRef).then(snapshot => {
            const batch = writeBatch(db);
            snapshot.forEach(doc => batch.delete(doc.ref));
            return batch.commit();
          });
          
          const deleteCatalogsPromise = getDocs(productCatalogsCollectionRef).then(snapshot => {
            const batch = writeBatch(db);
            snapshot.forEach(doc => batch.delete(doc.ref));
            return batch.commit();
          });

          await Promise.all([deleteProductsPromise, deleteCatalogsPromise]);

          // --- 新規データの登録処理 ---
          const newBatch = writeBatch(db);
          const catalogMap = new Map();
          let successCount = 0;
          const errors = [];

          productsFromCsv.forEach((product, index) => {
            const normalizedPartNumber = normalizePartNumber(product.partNumber);
            if (!normalizedPartNumber) { errors.push(`品番が空の行がありました。`); return; }
            
            const csvSequentialNum = product.sequentialNumber || product['連番'];
            const sequentialNum = csvSequentialNum ? Number(csvSequentialNum) : (index + 1);
            const sequentialNumStr = String(sequentialNum);

            const productData = {
              partNumber: normalizedPartNumber,
              majorCategory: product.majorCategory || '',
              middleCategory: product.middleCategory || '',
              manufacturer: product.manufacturer || '',
              userAttributeCode: product.userAttributeCode || '',
              listingStartDate: parseDateToTimestamp(product.listingStartDate),
              listingEndDate: parseDateToTimestamp(product.listingEndDate),
              stockQuantity: parseInt(product.stockQuantity, 10) || 0,
              lowerLimit: parseInt(product.lowerLimit, 10) || 0,
              addedBy: currentUser.email,
              addedAt: serverTimestamp(),
              importOrder: index, // CSV物理順序
              sequentialNumber: sequentialNum, // 連番を数値として保存
            };
            
            newBatch.set(doc(productsCollectionRef), productData);
            successCount++;
            
            // カタログキーのグループ化基準を連番のみに変更
            const catalogKey = `連番_${sequentialNumStr}`;
            
            if (catalogKey !== '_全点') {
              if (!catalogMap.has(catalogKey)) {
                catalogMap.set(catalogKey, { 
                    partNumbers: [], 
                    middleCategory: product.middleCategory || '', 
                    majorCategory: product.majorCategory || '',
                    sequentialNum: sequentialNum, // ★修正: 数値として保存
                    partNumberDetails: [],
                    importOrder: index // ★追加: カタログ内で一番最初に出てきた行番号を保存
                });
              }
              catalogMap.get(catalogKey).partNumbers.push(normalizedPartNumber);
              catalogMap.get(catalogKey).partNumberDetails.push({ partNumber: normalizedPartNumber, index: index });
            }
          });

          // 集計したカタログ情報をバッチに追加
          for (const [key, value] of catalogMap.entries()) {
            if (value.partNumbers.length > 0) {
              
              const sequentialNumPart = value.sequentialNum;
              
              // カタログ生成スキップ条件:
              // 連番がCSVで明示的に記載されていない場合、または連番が1の場合
              const isExplicitlyProvided = productsFromCsv.some(p => {
                  const checkNum = Number(p.sequentialNumber || p['連番']);
                  return checkNum === sequentialNumPart && !isNaN(checkNum);
              });

              if (!isExplicitlyProvided || sequentialNumPart === 1) {
                 console.log(`Skipping catalog creation: Sequential number is 1 or not explicitly provided for group (${key})`);
                 continue;
              }

              if (!value.middleCategory || !value.majorCategory) {
                 console.log(`Skipping catalog creation: middleCategory or majorCategory is missing for group (${key})`);
                 continue;
              }
              
              // CSV順に基づいてシリーズ開始・終了品番を設定
              const sortedByCsvOrder = value.partNumberDetails.sort((a, b) => a.index - b.index);
              
              const seriesStartDate = sortedByCsvOrder[0].partNumber;
              const seriesEndDate = sortedByCsvOrder[sortedByCsvOrder.length - 1].partNumber;
              
              const middleCategory = value.middleCategory;
              const majorCategory = value.majorCategory;
              
              const newCatalogName = `${middleCategory}_${majorCategory}全点_${seriesStartDate}~`;
              
              const catalogData = {
                catalogName: newCatalogName,
                seriesStartDate: seriesStartDate,
                seriesEndDate: seriesEndDate,
                seriesCount: value.partNumbers.length,
                missingPartNumbers: [],
                sequentialNum: sequentialNumPart, // 数値として保存
                importOrder: value.importOrder // ★追加: ソート用にCSV行番号を保存
              };
              newBatch.set(doc(productCatalogsCollectionRef), catalogData);
            }
          }
          
          await newBatch.commit();
          
          setUploadResult({ total: productsFromCsv.length, success: successCount, catalogs: catalogMap.size, errors: errors });
          fetchData();
        } catch (err) {
          console.error("一括登録エラー:", err);
          setError(`一括登録中にエラーが発生しました: ${err.message}`);
        } finally {
          setUploading(false);
        }
      },
      error: (err) => {
        console.error("CSV解析エラー:", err);
        setError(`CSVファイルの解析に失敗しました: ${err.message}`);
        setUploading(false);
      }
    });
  };

  // --- 表示用データのフィルタリングとページネーション ---

  const filteredProducts = useMemo(() => products.filter(p => 
    Object.values(p).some(val => String(val).toLowerCase().includes(productSearchTerm.toLowerCase()))
  ), [products, productSearchTerm]);

  const filteredCatalogs = useMemo(() => catalogs.filter(c => 
    c.catalogName.toLowerCase().includes(catalogSearchTerm.toLowerCase())
  ), [catalogs, catalogSearchTerm]);

  const paginatedProducts = useMemo(() => filteredProducts.slice((productCurrentPage - 1) * itemsPerPage, productCurrentPage * itemsPerPage), [filteredProducts, productCurrentPage]);
  
  const paginatedCatalogs = useMemo(() => filteredCatalogs.slice((catalogCurrentPage - 1) * itemsPerPage, catalogCurrentPage * itemsPerPage), [filteredCatalogs, catalogCurrentPage]);


  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">
            商品・カタログ管理
          </Card.Header>
          <Card.Body className="p-4">
            <section className="mb-5">
              <h5 className="mb-3">
                商品一括登録 (CSV)
              </h5>
              <Alert variant="warning">
                <strong>
                  注意:
                </strong>
                この操作を行うと、既存の全てのデータが削除され、このCSVの内容に完全に置き換えられます。
                <br/>
                データの移行が完了するまで時間がかかる場合があります。
              </Alert>
              <Card.Text as="div">
                <strong>
                  ヘッダー構成
                </strong>
                <br/>
                <small>
                  partNumber, majorCategory, middleCategory, manufacturer, userAttributeCode, listingStartDate, listingEndDate, stockQuantity, lowerLimit, <strong>sequentialNumber</strong>(任意)
                </small>
                <br/>
                <small className='text-danger'>※追加者・追加日は自動で反映されます。連番がない場合は行番号が使用されます。</small>
              </Card.Text>
              <Form.Group controlId="formFile" className="my-4">
                <Form.Label>
                  CSVファイルを選択
                </Form.Label>
                <Form.Control type="file" accept=".csv" onChange={handleFileChange} />
              </Form.Group>
              <div className="d-grid">
                <Button variant="danger" onClick={handleUpload} disabled={uploading}>
                  {
                    uploading ?
                    <Spinner as="span" size="sm" />
                    :
                    'CSVで全データ上書き登録'
                  }
                </Button>
              </div>
              {error && <Alert variant="danger" className="mt-4">{error}</Alert>}
              {uploadResult && (
                  <Alert variant="success" className="mt-4">
                    <Alert.Heading>
                      アップロード完了
                    </Alert.Heading>
                    <p>
                      {uploadResult.total}件中、{uploadResult.success}件の商品を登録しました
                    <br/>
                    また、{uploadResult.catalogs}件のカタログ情報を生成・登録しました。
                    </p>
                    {uploadResult.errors.length > 0 && (
                        <>
                          <hr />
                          <p className="mb-0">
                            以下のエラー/警告が発生しました:
                          </p>
                          <ListGroup variant="flush">
                            {uploadResult.errors.map((e, i) => 
                              <ListGroup.Item key={i} className="bg-transparent text-danger">
                                {e}
                              </ListGroup.Item>
                            )}
                          </ListGroup></>
                      )
                    }
                  </Alert>
                )
              }
            </section>
            <hr />
            
            <section>
              <h5 className="mb-3">
                登録済みデータ一覧
              </h5>
              {loadingLists ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : (
                  <Tabs defaultActiveKey="products" id="data-tabs" className="mb-3">
                    <Tab eventKey="products" title={`商品リスト (${filteredProducts.length})`}>
                      <Form.Control
                        type="text"
                        placeholder="商品リストを検索..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        className="mb-3"
                      />
                      <Table striped bordered hover responsive size="sm">
                        <thead>
                          <tr>
                            <th>No.</th>
                            <th>品番</th>
                            <th>大分類</th>
                            <th>中分類</th>
                            <th>メーカー</th>
                            <th>権限コード</th>
                            <th>掲載開始日</th>
                            <th>掲載終了日</th>
                            <th>追加者</th>
                            <th>追加日</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedProducts.map(p => (
                              <tr key={p.id}>
                                <td>{p.sequentialNumber}</td>
                                <td>{p.partNumber}</td>
                                <td>{p.majorCategory}</td>
                                <td>{p.middleCategory}</td>
                                <td>{p.manufacturer}</td>
                                <td>{p.userAttributeCode}</td>
                                <td>{p.listingStartDate ? p.listingStartDate.toDate().toLocaleDateString('ja-JP') : ''}</td>
                                <td>{p.listingEndDate ? p.listingEndDate.toDate().toLocaleDateString('ja-JP') : ''}</td>
                                <td>{usersMap.get(p.addedBy) || p.addedBy}</td>
                                <td>{p.addedAt ? p.addedAt.toDate().toLocaleDateString('ja-JP') : ''}</td>
                              </tr>
                          ))}
                        </tbody>
                      </Table>
                      <CustomPagination
                        currentPage={productCurrentPage}
                        totalPages={Math.ceil(filteredProducts.length / itemsPerPage)}
                        paginate={setProductCurrentPage}
                      />
                    </Tab>
                    
                    <Tab eventKey="catalogs" title={`カタログリスト (${filteredCatalogs.length})`}>
                      <Form.Control
                        type="text"
                        placeholder="カタログリストを検索..."
                        value={catalogSearchTerm}
                        onChange={(e) => setCatalogSearchTerm(e.target.value)}
                        className="mb-3"
                      />
                      <Table striped bordered hover responsive size="sm">
                        <thead>
                          <tr>
                            <th>カタログ名</th>
                            <th>連番</th>
                            <th>シリーズ開始品番</th>
                            <th>シリーズ終了品番</th>
                            <th>総数</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedCatalogs.map(c => (
                              <tr key={c.id}>
                                  <td>{c.catalogName}</td>
                                  <td>{c.sequentialNum || '-'}</td>
                                  <td>{c.seriesStartDate}</td>
                                  <td>{c.seriesEndDate}</td>
                                  <td>{c.seriesCount}</td>
                                </tr>
                              )
                            )
                          }
                        </tbody>
                      </Table>
                      <CustomPagination
                        currentPage={catalogCurrentPage}
                        totalPages={Math.ceil(filteredCatalogs.length / itemsPerPage)}
                        paginate={setCatalogCurrentPage}
                      />
                    </Tab>
                  </Tabs>
                )
              }
            </section>

            <hr className="my-4" />
            <Link to="/admin">管理者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default ProductAdmin;